import { ChipLink } from '@src/components/seo/layout';
import type { DRepStatus } from '@src/lib/seo/catalog';

/**
 * Status filter for the DRep directory.
 *
 * Defaults to Active, which is why `/dreps` carries no query string for it:
 * the directory is overwhelmingly registrations that never voted or have since
 * retired — 158 Active out of 10,000 on preview — so listing all of them first
 * buried everyone worth delegating to.
 */
export type DRepStatusFilter = DRepStatus | 'All';

export const DREP_STATUS_FILTERS: { value: DRepStatusFilter; label: string }[] =
  [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Retired', label: 'Retired' },
    { value: 'All', label: 'All' },
  ];

export const DEFAULT_DREP_STATUS: DRepStatusFilter = 'Active';

export function parseDRepStatus(value?: string | null): DRepStatusFilter {
  const match = DREP_STATUS_FILTERS.find(
    (filter) => filter.value.toLowerCase() === value?.toLowerCase()
  );
  return match?.value ?? DEFAULT_DREP_STATUS;
}

/** One URL per view: the default status is the bare path. */
export function drepsHref(
  status: DRepStatusFilter,
  query?: string,
  page?: number
): string {
  const params = new URLSearchParams();
  if (status !== DEFAULT_DREP_STATUS)
    params.set('status', status.toLowerCase());
  if (query) params.set('q', query);
  if (page && page > 1) params.set('page', String(page));
  const suffix = params.toString();
  return suffix ? `/dreps?${suffix}` : '/dreps';
}

export default function DRepFilters({
  status,
  query,
}: {
  status: DRepStatusFilter;
  query?: string;
}) {
  return (
    <nav aria-label="DRep status" className="flex flex-wrap items-center gap-2">
      <span className="label12 w-14 shrink-0 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500">
        Status
      </span>
      {DREP_STATUS_FILTERS.map((filter) => (
        <ChipLink
          key={filter.value}
          href={drepsHref(filter.value, query)}
          active={filter.value === status}
        >
          {filter.label}
        </ChipLink>
      ))}
    </nav>
  );
}
